import React from "react";
import { Box, Typography, CardContent, useTheme } from "@mui/material";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import { StyledCard } from "../../common/StyledCard";
import { formatCurrency } from "../../../utils/currencyUtils";
import { DailySalesTrendBiDto } from "../../../shared/dtos/bi/DailySalesTrendBiDto";

interface SalesTrendsWidgetProps {
  data?: DailySalesTrendBiDto[];
}

const SalesTrendsWidget: React.FC<SalesTrendsWidgetProps> = ({ data = [] }) => {
  const theme = useTheme();
  const colorRevenue = theme.palette.secondary.main;
  const colorProfit = "#ff9800";

  const formatDate = (dateStr: unknown): string => {
    if (typeof dateStr !== "string") return "";
    const date = new Date(dateStr);
    return date.toLocaleDateString("en-US", { month: "short", day: "numeric" });
  };

  return (
    <StyledCard
      sx={{ height: "100%", display: "flex", flexDirection: "column" }}
    >
      <CardContent
        sx={{ flexGrow: 1, display: "flex", flexDirection: "column" }}
      >
        <Typography variant="h6" sx={{ fontWeight: "bold", mb: 3 }}>
          Sales & Profit Trends
        </Typography>

        {data.length > 0 ? (
          <Box sx={{ flexGrow: 1, width: "100%", minHeight: 350 }}>
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart
                data={data}
                margin={{ top: 10, right: 30, left: 10, bottom: 0 }}
              >
                <defs>
                  <linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1">
                    <stop
                      offset="5%"
                      stopColor={colorRevenue}
                      stopOpacity={0.8}
                    />
                    <stop
                      offset="95%"
                      stopColor={colorRevenue}
                      stopOpacity={0.1}
                    />
                  </linearGradient>
                  <linearGradient id="colorProfit" x1="0" y1="0" x2="0" y2="1">
                    <stop
                      offset="5%"
                      stopColor={colorProfit}
                      stopOpacity={0.8}
                    />
                    <stop
                      offset="95%"
                      stopColor={colorProfit}
                      stopOpacity={0.1}
                    />
                  </linearGradient>
                </defs>

                <CartesianGrid
                  strokeDasharray="3 3"
                  vertical={false}
                  stroke={theme.palette.neutral.dark}
                />

                <XAxis
                  dataKey="date"
                  tickFormatter={formatDate}
                  tick={{ fontSize: 12, fill: theme.palette.neutral.light }}
                  tickMargin={10}
                />
                <YAxis
                  tickFormatter={(value) =>
                    new Intl.NumberFormat("en-US", {
                      notation: "compact",
                    }).format(value)
                  }
                  tick={{ fontSize: 12, fill: theme.palette.neutral.light }}
                  tickMargin={10}
                />

                <Tooltip
                  cursor={{
                    stroke: theme.palette.neutral.light,
                    strokeWidth: 1,
                    strokeDasharray: "3 3",
                  }}
                  contentStyle={{
                    backgroundColor: theme.palette.background.paper,
                    borderRadius: 8,
                    border: `1px solid ${theme.palette.neutral.dark}`,
                    color: theme.palette.neutral.light,
                  }}
                  labelFormatter={formatDate}
                  formatter={(value, name, props) => [
                    formatCurrency(Number(value)),
                    name === "totalRevenue"
                      ? `Revenue (${props.payload.transactionCount} txns)`
                      : "Profit",
                  ]}
                />

                <Legend
                  wrapperStyle={{
                    paddingTop: "15px",
                    color: theme.palette.neutral.light,
                  }}
                />

                <Area
                  type="monotone"
                  dataKey="totalRevenue"
                  name="Revenue"
                  stroke={colorRevenue}
                  strokeWidth={3}
                  fillOpacity={1}
                  fill="url(#colorRevenue)"
                />
                <Area
                  type="monotone"
                  dataKey="totalProfit"
                  name="Profit"
                  stroke={colorProfit}
                  strokeWidth={3}
                  fillOpacity={1}
                  fill="url(#colorProfit)"
                />
              </AreaChart>
            </ResponsiveContainer>
          </Box>
        ) : (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexGrow: 1,
              minHeight: 350,
            }}
          >
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ fontStyle: "italic" }}
            >
              No sales trend data available.
            </Typography>
          </Box>
        )}
      </CardContent>
    </StyledCard>
  );
};

export default SalesTrendsWidget;
